import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

export type AcquisitionKpisFilters = {
  start_date: string;
  end_date: string;
  // v1 BE contract: single-value `state[eq]=<code>` query, or omitted for
  // "no filter" (equivalent to sending `state[eq]=all`). Valid codes come
  // from `meta.filters[0].options` on either acquisition response — read
  // them via `useGetAcquisitionSources`.
  state?: string;
};

export type AcquisitionKpiCard = {
  key: AcquisitionKpiKey;
  value: number;
};

// Order is the order the UTM Reports KPI grid renders. Keep it stable —
// the page maps each entry to its title / formatter via this list.
export const ACQUISITION_KPI_KEYS = [
  "deposit_amount",
  "withdrawal_amount",
  "revenue",
  "total_users",
  "total_ftds",
  "avg_net_revenue",
] as const;

export type AcquisitionKpiKey = (typeof ACQUISITION_KPI_KEYS)[number];

const coerceNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

// UTM filters are applied client-side (not BE params in v1); state is
// `state[eq]=<code>` and the date range is forwarded as-is.
const buildParams = (filters: AcquisitionKpisFilters) => ({
  start_date: filters.start_date,
  end_date: filters.end_date,
  ...(filters.state ? { "state[eq]": filters.state } : {}),
});

/**
 * Maps the `{ data: { <kpi_key>: <number> } }` envelope into the shape
 * the KPI grid consumes. Unknown KPI keys are dropped — adding a new
 * card means adding it to `ACQUISITION_KPI_KEYS`.
 *
 * The BE doesn't ship period-over-period change in v1, so the page
 * renders these cards without the delta badge.
 */
export const parseAcquisitionKpisResponse = (
  body: unknown,
): AcquisitionKpiCard[] => {
  const envelope = body as { data?: unknown } | null | undefined;
  const payload = envelope?.data;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return [];
  }

  const record = payload as Record<string, unknown>;

  return ACQUISITION_KPI_KEYS
    .filter((key) => key in record)
    .map((key) => ({ key, value: coerceNumber(record[key]) }));
};

export const fetchAcquisitionKpis = async (
  filters: AcquisitionKpisFilters,
): Promise<AcquisitionKpiCard[]> => {
  const instance = getPrivateInstance();
  const response = await instance.get(API_ROUTES.PROMOTIONS_ACQUISITION_KPIS, {
    params: buildParams(filters),
  });
  return parseAcquisitionKpisResponse(response?.data);
};

export const useGetAcquisitionKpis = (filters: AcquisitionKpisFilters) => {
  const { start_date, end_date, state } = filters;

  return useQuery<AcquisitionKpiCard[], Error>({
    queryKey: [
      "PROMOTIONS_ACQUISITION_KPIS",
      start_date,
      end_date,
      state ?? "",
    ],
    queryFn: () => fetchAcquisitionKpis(filters),
    enabled: Boolean(start_date && end_date),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
